import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import InfiniteScroll from '@components/infinite-scroll/InfiniteScroll';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import TimeLineError from './TimeLineError';
import TimeLineLoader from './TimeLineLoader';
import TimelineStepper from './TimelineStepper';

import { LEAD_ACTIVITY_LIMIT } from '../../constants/leadActivity';
import { useLazyGetLeadTimelineQuery } from '../../queries/LeadManagementQuery';
import { TimelineItemAPI, TimelineTabProps } from '../../types/leadActivity';

const TimelineTab: React.FC<TimelineTabProps> = ({ leadId }) => {
  const organizationId = getLocalStorageItem(STORAGE_KEYS.ORGANIZATION_ID);
  const { currentOrg } = useSelector((state: any) => state.user);
  const { createErrorAlert } = useNotify();

  const [loadMoreTimeline, { isLoading, isFetching }] =
    useLazyGetLeadTimelineQuery();

  const [page, setPage] = useState<number>(0);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [allTimelineData, setAllTimelineData] = useState<TimelineItemAPI[]>([]);
  const [isPaginationLoading, setIsPaginationLoading] =
    useState<boolean>(false);
  const [error, setError] = useState<any>(null);
  const hasTrackedTimeline = useRef(false);

  const handleScroll = () => {
    setPage((prev) => prev + 1);
  };

  const handleGetTimeline = useCallback(
    async (isPagination: boolean) => {
      if (isPagination) {
        setIsPaginationLoading(true);
      }

      try {
        const res = await loadMoreTimeline({
          organizationId: organizationId || '',
          leadId,
          page: isPagination ? page : 0,
          limit: LEAD_ACTIVITY_LIMIT
        });

        if (res.data) {
          const newTimelineData = res.data.data ?? [];
          if (leadId && !hasTrackedTimeline.current) {
            hasTrackedTimeline.current = true;
            sendMixpanelEvent(Event.LEAD_TIMELINE_VIEWED, {
              org_id: organizationId,
              org_name: currentOrg?.name,
              lead_id: leadId,
              timeline_count: res.data.pagination?.total_rows ?? 0
            });
          }
          if (isPagination) {
            setIsPaginationLoading(false);
          }

          setAllTimelineData((previousData) => {
            if (isPagination) {
              return [...previousData, ...newTimelineData];
            }
            return newTimelineData;
          });

          setDisablePagination(res.data?.pagination?.last_page === true);
          setError(null);
        }
      } catch (loadError) {
        if (isPagination) {
          setIsPaginationLoading(false);
        }
        setError(loadError);
        createErrorAlert('Failed to load timeline data. Please try again.');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadMoreTimeline, organizationId, leadId, page, createErrorAlert]
  );

  const handleRefetch = useCallback(() => {
    setPage(0);
    setAllTimelineData([]);
    setDisablePagination(false);
    setError(null);
    handleGetTimeline(false);
  }, [handleGetTimeline]);

  useEffect(() => {
    handleGetTimeline(page !== 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    hasTrackedTimeline.current = false;
    handleGetTimeline(false);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, organizationId]);

  const renderContent = useCallback(() => {
    if (
      (isLoading || isFetching) &&
      !isPaginationLoading &&
      allTimelineData.length === 0
    ) {
      return <TimeLineLoader />;
    }

    if (error) {
      return <TimeLineError handleRefetch={handleRefetch} />;
    }

    if (allTimelineData.length > 0) {
      return (
        <div>
          <TimelineStepper timelineData={allTimelineData} />
          <InfiniteScroll
            handleScroll={handleScroll}
            isDisable={disablePagination}
            isLoading={isPaginationLoading}
            spinnerStyle="w-6 h-6"
          />
        </div>
      );
    }

    return (
      <div className="py-8 text-center text-gray-500">
        No timeline data available
      </div>
    );
  }, [
    isLoading,
    isFetching,
    isPaginationLoading,
    allTimelineData,
    error,
    disablePagination,
    handleRefetch
  ]);

  return (
    <div className="custom-scroll max-h-175 w-full overflow-y-auto overflow-x-hidden rounded-xl bg-white p-6">
      {renderContent()}
    </div>
  );
};

export default TimelineTab;
